import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { IRootState } from 'src/reducers';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import moment from 'moment';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import { UserRoles } from 'src/models/UserRole';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';
import PersonnelToast from '../components/PersonnelToast';
import LandmanBillInfo from 'src/models/personnel/LandmanBillInfo';
import LandmanPayInfo from 'src/models/personnel/LandmanPayInfo';
import PersonnelPayInfo from 'src/models/personnel/PersonnelPayInfo';
import DefaultBillHistoryDialog from '../dialogs/pay/DefaultBillHistoryDialog';
import DefaultPayHistoryDialog from '../dialogs/pay/DefaultPayHistoryDialog';
import UpdateBillHistoryDialog from '../dialogs/pay/UpdateDefaultBillPayDialog';
import UpdatePayHistoryDialog from '../dialogs/pay/UpdateDefaultContractPayDialog';
import PersonnelCommentDialog from '../dialogs/PersonnelPerformanceDialog';
import LandmanPayException from 'src/models/personnel/LandmanPayException';
import ProjectPayExceptionHistoryDialog from '../dialogs/pay/ProjectPayExceptionAndHistoryDialog';
import TSDILandman from 'src/models/personnel/TSDILandman';
import ProjectBillExceptionAndHistoryDialog from '../dialogs/pay/ProjectBillExceptionAndHistoryDialog';
import LandmanBillException from 'src/models/personnel/LandmanBillException';

type DropDownSelection = {
    code: string,
    dscr: string
};

const EmployeePayPage = () => {
    const dispatch = useDispatch();
    const { get, put, response } = useFetch();
    const { register, reset, handleSubmit, errors, getValues, setValue } = useForm();

    // Pay
    const [selectedEmployeePay, setSelectedEmployeePay] = useState<PersonnelPayInfo>(undefined);
    const [payMethods, setPayMethods] = useState<Array<DropDownSelection>>(undefined);
    const [allPositions, setAllPositions] = useState<Array<DropDownSelection>>(undefined);

    // Contract / Billing
    const [defaultBillHistory, setDefaultBillHistory] = useState<Array<LandmanBillInfo>>(undefined);
    const [currentDefaultBill, setCurrentDefaultBill] = useState<LandmanBillInfo>(undefined);
    const [defaultPayHistory, setDefaultPayHistory] = useState<Array<LandmanPayInfo>>(undefined);
    const [currentDefaultPay, setCurrentDefaultPay] = useState<LandmanPayInfo>(undefined);

    const [payUnitProp, setPayUnitProp] = useState<string>(undefined);
    const [payRateProp, setPayRateProp] = useState<number>(undefined);
    const [billUnitProp, setBillUnitProp] = useState<string>(undefined);
    const [billRateProp, setBillRateProp] = useState<number>(undefined);

    const [payExceptions, setPayExceptions] = useState<Array<LandmanPayException>>(undefined);
    const [billExceptions, setBillExceptions] = useState<Array<LandmanBillException>>(undefined);

    // Misc
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const appContext = useContext<IAppContextState>(AppContext);

    const isUserHR =
        appContext.currentUser.role === UserRoles.HR ||
        appContext.currentUser.role === UserRoles.HRAdmin ||
        appContext.currentUser.role === UserRoles.Admin;

    let selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);

    const sideBarHeight = document.getElementById('positionSideBar')?.offsetHeight;
    const sideBarHeightSize = sideBarHeight + 'px';

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    // SelectedPersonnel will change when the user clicks on a user or on initial render, if url contains an id
    useEffect(() => {
        if (!!selectedPersonnel) {
            // reset();
            loadData();
            console.log('====> SelectedPersonnel = ', selectedPersonnel);
        }
    }, [selectedPersonnel]);

    // SelectedEmployeePay changes on Update Pay
    useEffect(() => {
        if (selectedEmployeePay) {
            const data = initializeData(selectedEmployeePay);
            const formatCurrency = (val: number) =>
                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);
            reset({
                ...data,
                guaranteed: formatCurrency(data.guaranteed),
                salary: formatCurrency(data.salary),
                dayRate: formatCurrency(data.dayRate),
                hourRate: formatCurrency(data.hourRate),
                defPayRate: formatCurrency(data.defPayRate),
                billDayRate: formatCurrency(data.billDayRate),
            });
            setValue('position', data.position);
        }
    }, [selectedEmployeePay]);

    useEffect(() => {
        if (!!currentDefaultPay) {
            setPayRateProp(currentDefaultPay?.payRate ?? undefined);
            setPayUnitProp(currentDefaultPay?.payUnit ?? undefined);
        }
    }, [currentDefaultPay]);

    useEffect(() => {
        if (!!currentDefaultBill) {
            setBillRateProp(currentDefaultBill?.billRate ?? undefined);
            setBillUnitProp(currentDefaultBill?.billUnit ?? undefined);
        }
    }, [currentDefaultBill]);

    useEffect(() => {
        if (defaultPayHistory?.length > 0) {
            console.log('====> defaultPayHistory = ', defaultPayHistory);
            let currentDF = defaultPayHistory[0];

            if (currentDF) {
                setCurrentDefaultPay(currentDF);
                setValue('defPayRate',
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(currentDF.payRate ?? 0));
                setValue('paidBy', currentDF.payUnit);
            } else {
                setCurrentDefaultPay(undefined);
            }
        }
    }, [defaultPayHistory]);

    useEffect(() => {
        if (defaultBillHistory?.length > 0) {
            console.log('====> defaultBillHistory = ', defaultBillHistory);
            let currentDF = defaultBillHistory[0];

            if (currentDF) {
                setCurrentDefaultBill(currentDF);
                setValue('billDayRate',
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(currentDF.billRate ?? 0));
            }
        } else {
            setCurrentDefaultBill(undefined);
        }
    }, [defaultBillHistory]);

    /**
     * This function will be called when the Page is first rendered or the user enters the Page
     */
    const onCreate = () => {
        console.log('PersonnelPage onCreate()');
        if (!!selectedPersonnel) {
            loadData();
        }
    };

    /**
     * This function will be called when the user leaves the Page
     */
    const onDestroy = () => {
        console.log('PersonnelPage onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // loads data into dropdowns
    const loadData = async () => {
        setIsLoading(true);
        await getPayMethods();
        // await getAllPositions();
        await getDefaultBillHistory();
        await getDefaultPayHistory();

        let payInfo = await getPersonnelPayInformation();
        await getAllPositions(payInfo);

        await getAllPayExceptions();
        await getAllBillExceptions();
        setIsLoading(false);
    };

    // initialize and reset form data
    const initializeData = (data: PersonnelPayInfo) => {
        if (!data) {
            return {} as PersonnelPayInfo;
        }
        return {
            ...data,
            position: selectedEmployeePay.position.map(String),
            payMethod: data.payMethod ? data.payMethod?.toString() : '',
            modDate: data.modDate ? (moment(data.modDate).format('YYYY-MM-DD') as unknown as Date) : null,
            effDate: data.effDate ? (moment(data.effDate).format('YYYY-MM-DD') as unknown as Date) : null,
            birthDate: data.birthDate ? (moment(data.birthDate).format('YYYY-MM-DD') as unknown as Date) : null,
        };
    };

    // employee pay info
    const getPersonnelPayInformation = async () => {
        let result = await get(`v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/get-personnel-pay-info`);

        if (response.ok) {
            setSelectedEmployeePay(result);
            return result;
        } else {
            showToast('Unable to fetch personnel\'s pay information', `rgba(255,0,0,0.5)`);
            return undefined;
        }
    };

    // all pay exceptions
    const getAllPayExceptions = async () => {
        let result = await get(`v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/get-pay-exceptions`);

        if (response.ok) {
            setPayExceptions(result);
        } else {
            showToast('Unable to fetch personnel\'s pay exception information', `rgba(255,0,0,0.5)`);
        }
    };

    // all bill rate exceptions
    const getAllBillExceptions = async () => {
        let result = await get(`v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/get-bill-exceptions`);

        if (response.ok) {
            setBillExceptions(result);
        } else {
            showToast('Unable to fetch personnel\'s bill exception information', `rgba(255,0,0,0.5)`);
        }
    };

    // pay methods - maps to dropdown format
    const getPayMethods = async () => {
        let results = await get(`v1.0/landmen/pay-methods`);
        let filteredTypes = ['6']; // per hr, no need for "prospect manager" pay type

        if (response.ok && results) {
            setPayMethods(results.map((ct: DropDownSelection) => {
                return { code: ct.code, dscr: ct.dscr };
            }).filter((ct: DropDownSelection) => !filteredTypes.includes(ct.code)));
        } else {
            showToast('Unable to fetch pay methods', `rgba(255,0,0,0.5)`);
        }
    };

    const payMethodsDropdown = () => {
        return payMethods
            ? payMethods?.map((ct: DropDownSelection) => {
                return { value: ct.code, label: ct.dscr };
            })
            : [];
    };

    // positions
    const getAllPositions = async (employeePay?: PersonnelPayInfo) => {
        let results = await get(`v1.0/landmen/positions`);
        let includeList = ['26', '47', '32', '46', '22', '45', '71', '63',
            '38', '56', '37', '48', '49', '52', '64', '24', '17', '57',
            '11', '54', '1', '25', '9', '70', '43', '42', '58', '2', '30', '61', '67', '5', '36']; // list provided by hr

        // renaming positions (per hr)
        results?.forEach((x: DropDownSelection) => {
            switch (x.code.toString()) {
                case '63':
                    x.dscr = 'Director - B.D';
                    break;
                case '48':
                    x.dscr = 'H.R. Manager';
                    break;
                case '49':
                    x.dscr = 'IT & App Dev Manager';
                    break;
                case '2':
                    x.dscr = 'EVP';
                    break;
                default:
                    break;
            }
        });

        if (response.ok && results) {
            setAllPositions(
                results
                    .filter((x: DropDownSelection) => includeList.includes(x.code) || employeePay?.position.includes(parseInt(x.code, 10)))
                    .map((ct: DropDownSelection) => {
                        return { code: ct.code, dscr: ct.dscr };
                    })
                    .sort((a: DropDownSelection, b: DropDownSelection) => {
                        if (a.dscr < b.dscr) {
                            return -1;
                        }
                        if (a.dscr > b.dscr) {
                            return 1;
                        }
                        return 0;
                    }));
        } else {
            showToast('Unable to fetch pay methods', `rgba(255,0,0,0.5)`);
        }
    };

    const onSubmit = async (data: PersonnelPayInfo) => {
        if (!!data) {
            let finalData = finalizeData(data);
            console.log(finalData);
            updateEmployeePay(finalData);
            // updatePersonnelBilling(finalData.employeePayExceptions);
        }
    };

    const finalizeData = (data: PersonnelPayInfo) => {
        let finalData: PersonnelPayInfo = {
            ...data
        };

        let values = getValues();

        let formattedBillDayRate = values.billDayRate ? values.billDayRate.replace(/[^0-9.]/g, '') : null;
        let formattedDayRate = values.dayRate ? values.dayRate.replace(/[^0-9.]/g, '') : null;
        let formattedDefPayRate = values.defPayRate ? values.defPayRate.replace(/[^0-9.]/g, '') : null;
        let formattedHourRate = values.hourRate ? values.hourRate.replace(/[^0-9.]/g, '') : null;
        let formattedGuaranteed = values.guaranteed ? values.guaranteed.replace(/[^0-9.]/g, '') : null;
        let formattedSalary = values.salary ? values.salary.replace(/[^0-9.]/g, '') : null;

        finalData.billDayRate = values.billDayRate ? parseFloat(formattedBillDayRate) : null;
        finalData.dayRate = values.dayRate ? parseFloat(formattedDayRate) : null;
        finalData.defPayRate = values.defPayRate ? parseFloat(formattedDefPayRate) : null;
        finalData.guaranteed = values.guaranteed ? parseFloat(formattedGuaranteed) : null;
        finalData.hourRate = values.hourRate ? parseFloat(formattedHourRate) : null;
        finalData.salary = values.salary ? parseFloat(formattedSalary) : null;

        finalData.payMethod = values.payMethod ? parseInt(values.payMethod, 10) : null;
        finalData.position = values.position ? values.position.map((val: string) => parseInt(val, 10)) : [];

        finalData.modDate = finalData.modDate ? (moment(new Date()).format('YYYY-MM-DD') as unknown as Date) : null;
        finalData.effDate = finalData.effDate ? (moment(new Date()).format('YYYY-MM-DD') as unknown as Date) : null;
        finalData.prodBonus = finalData.prodBonus ? finalData.prodBonus : false;

        finalData.manager = finalData.manager ? finalData.manager : false;
        finalData.paidBy = finalData.paidBy ? finalData.paidBy : null;

        return finalData;
    };

    const updateEmployeePay = async (data: PersonnelPayInfo) => {
        await put(`v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/update-personnel-pay-info`, data);

        if (response.ok) {
            showToast('Updated personnel\'s pay successfully');
        } else {
            showToast('Unable to update personnel\'s pay information', `rgba(255,0,0,0.5)`);
        }
    };

    // start dialogs
    const showEmployeePayHistory = async () => {
        if (!!selectedPersonnel) {
            dispatch(actions.layout.showDialog(DialogList.DEFAULT_PAY_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const showUpdateEmployeePay = async () => {
        if (!!selectedPersonnel) {
            dispatch(actions.layout.showDialog(DialogList.UPDATE_PAY_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const showEmployeBillingHistory = async () => {
        if (!!selectedPersonnel) {
            dispatch(actions.layout.showDialog(DialogList.DEFAULT_BILL_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const showUpdateEmployeeBilling = async () => {
        if (!!selectedPersonnel) {
            dispatch(actions.layout.showDialog(DialogList.UPDATE_BILLING_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const showPerformanceComments = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_PERFORMANCE_COMMENTS));
    };

    const showProjectPayExceptionsHistory = () => {
        dispatch(actions.layout.showDialog(DialogList.PAY_EXCEPTIONS_AND_HISTORY));
    };

    const showProjectBillExceptionsHistory = () => {
        dispatch(actions.layout.showDialog(DialogList.BILL_EXCEPTIONS_AND_HISTORY));
    };

    const showPersonnelData = () => {
        console.log('Display the Stuffs');
    };

    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };
    // end dialogs

    const updateScreenPay = () => {
        showToast('Contract pay update successful');
        getDefaultPayHistory();
        getPersonnelPayInformation();
    };

    const getDefaultPayHistory = async () => {
        // setIsLoading(true);
        // let data = await get(`v1.0/landmen/${selectedPersonnel?.id}/get-defaultPay-history`);
        if (selectedPersonnel?.lmNo !== undefined && selectedPersonnel?.lmNo !== null && selectedPersonnel?.lmNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${selectedPersonnel.lmNo}/get-pay-history`);

            if (response.ok) {
                setDefaultPayHistory(data);
            } else {
                showToast('Unable to get selected personnel\'s pay history information', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided', `rgba(255,0,0,0.5)`);
        }

        // setIsLoading(false);
    };

    const updateScreenBilling = () => {
        showToast('Contract pay update successful');
        getDefaultBillHistory();
    };

    const getDefaultBillHistory = async () => {
        // setIsLoading(true);
        // let data = await get(`v1.0/landmen/${selectedPersonnel?.id}/get-defaultBill-history`);

        if (selectedPersonnel?.lmNo !== undefined && selectedPersonnel?.lmNo !== null && selectedPersonnel?.lmNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${selectedPersonnel.lmNo}/get-bill-history`);

            if (response.ok) {
                setDefaultBillHistory(data);
            } else {
                showToast('Unable to get selected personnel\'s billing history information', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided', `rgba(255,0,0,0.5)`);
        }

        // setIsLoading(false);
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Employee Pay - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Employee Pay';

        return title;
    };

    // format guarenteed value to always have a dollar sign in front of the amount
    const formatGuaranteed = (e: ChangeEvent<HTMLInputElement>) => {
        let guaranteed: string = e.target.value;

        if (!guaranteed.includes('$')) {
            setValue('guaranteed', `${'$'}${guaranteed}`);
        }
    };

    const displayPayExceptions = () => {
        return <div>
            <table style={{ width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ width: '5%' }}>Client</th>
                        <th style={{ width: '20%' }}>Client Name</th>
                        <th style={{ width: '5%' }}>Project</th>
                        <th style={{ width: '20%' }}>Project Name</th>
                        <th style={{ width: '10%' }}>Pay Rate</th>
                        <th style={{ width: '5%' }}>Pay Unit</th>
                        <th style={{ width: '10%' }}>Classification</th>
                    </tr>
                </thead>
                {payExceptions?.map((history: LandmanPayException, index: number) => {
                    return (
                        <tbody key={`body-${index}`}>
                            <tr className="table-item-hover" style={{ textAlign: 'center' }}>
                                <td style={{ width: '5%' }}>
                                    {history.clNo}
                                </td>
                                <td style={{ width: '20%' }}>
                                    {history.clientName}
                                </td>
                                <td style={{ width: '5%' }}>
                                    {history.prNo}
                                </td>
                                <td style={{ width: '20%' }}>
                                    {history.projectName}
                                </td>
                                <td style={{ width: '10%' }}>
                                    {isUserHR && <RHFInputBox
                                        name={`employeePayExceptions[${index}].payRate`}
                                        type="number"
                                        defaultVal={`${history.payRate}`}
                                        register={register}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        bodyStyle={{ width: '100%' }}
                                        disabled={true}
                                    />}
                                    {!isUserHR && <label style={{ width: '100%' }}>NA</label>}
                                </td>
                                <td style={{ width: '5%' }}>
                                    {isUserHR && <RHFSelect
                                        name={`employeePayExceptions[${index}].payUnit`}
                                        defaultVal={history.payUnit}
                                        options={[
                                            { value: 'D', label: 'Day' },
                                            { value: 'H', label: 'Hour' },
                                            { value: 'M', label: 'Month' }
                                        ]}
                                        register={register}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        bodyStyle={{ width: '100%' }}
                                        disabled={true}
                                    />}
                                    {!isUserHR && <label style={{ width: '100%' }}>NA</label>}
                                </td>
                                <td style={{ width: '10%' }}>
                                    <RHFInputBox
                                        name={`employeePayExceptions[${index}].classificaton`}
                                        type="text"
                                        defaultVal={'TBD'}
                                        register={register}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        bodyStyle={{ width: '100%' }}
                                        disabled={true}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    );
                })}
            </table>
        </div>;
    };

    return (
        <div style={{ margin: '1rem' }}>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch showPersonnelData={showPersonnelData} setIsCollapsed={setIsCollapsed} toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <form id="employeePayForm" onSubmit={handleSubmit(onSubmit)}>
                <Page style={{ justifyContent: 'center' }}>
                    {/* Top left panel */}
                    <Page.Section width={6} height={8}>
                        <Panel backgroundColor="#D3D3D3" height="100%">
                            <Panel panelTitle={displayTitle()}>
                                {selectedPersonnel && <div style={isUserHR ? { padding: '.5rem', height: sideBarHeightSize } : { padding: '.5rem', paddingBottom: '2.5rem' }}>
                                    <div className="form-row" style={{ marginBottom: '0' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '24%', height: '3rem', marginLeft: '.5rem' }}
                                            text={'Update Pay'}
                                            onClick={() => showUpdateEmployeePay()}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '24%', height: '3rem', marginLeft: '1rem' }}
                                            text={'Pay History'}
                                            disabled={!isUserHR}
                                            onClick={() => showEmployeePayHistory()}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '24%', height: '3rem', marginLeft: '1rem' }}
                                            text={'Comments'}
                                            onClick={() => showPerformanceComments()}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '24%', height: '3rem', marginLeft: '.5rem' }}
                                            text={'Save Info'}
                                            type="submit"
                                            intent="primary"
                                            disabled={isLoading ? true : false}
                                        />
                                    </div>
                                    <div className="form-row" style={{ marginTop: '1rem', width: '100%' }}>
                                        <RHFSelect
                                            name={'payMethod'}
                                            options={payMethodsDropdown()}
                                            register={register}
                                            label={'Pay Method'}
                                            labelOnLeft={true}
                                            style={{ width: '80%' }}
                                            labelStyle={{ width: '20%' }}
                                        />
                                    </div>
                                    <div className="dialog-two-col">
                                        <div className="dialog-col">
                                            <RHFInputBox
                                                label="Change Date"
                                                name={`modDate`}
                                                type="date"
                                                register={register}
                                                errors={errors}
                                                style={{ width: 'auto', marginBottom: '1rem' }}
                                                disabled={true}
                                                labelOnLeft={true}
                                                spaceLabelContent={true}
                                            />
                                            <RHFInputBox
                                                label="Eff. Date"
                                                name={`effDate`}
                                                type="date"
                                                register={register}
                                                errors={errors}
                                                style={{ width: 'auto', marginBottom: '1rem' }}
                                                labelOnLeft={true}
                                                spaceLabelContent={true}
                                            />
                                            <RHFCheckbox
                                                name="prodBonus"
                                                label="Prod. Comp.?"
                                                register={register}
                                                errors={errors}
                                                labelOnLeft={true}
                                                style={{ width: 'auto', marginBottom: '1rem', verticalAlign: 'middle' }}
                                                labelStyle={{ textAlign: 'center' }}
                                            />
                                            <RHFInputBox
                                                label="Guaranteed"
                                                name={`guaranteed`}
                                                register={register}
                                                type="text"
                                                errors={errors}
                                                labelOnLeft={true}
                                                spaceLabelContent={true}
                                                style={{ width: 'auto', marginBottom: '1rem' }}
                                                onChange={formatGuaranteed}
                                            />
                                        </div>
                                        <div className="dialog-col">
                                            <RHFInputBox
                                                label="Annual Salary"
                                                name={`salary`}
                                                register={register}
                                                type="text"
                                                errors={errors}
                                                style={{ width: 'auto', marginBottom: '1rem' }}
                                                labelOnLeft={true}
                                                spaceLabelContent={true}
                                                disabled={true}
                                            />
                                            <RHFInputBox
                                                label="Company Day Rate"
                                                name={`dayRate`}
                                                register={register}
                                                type="text"
                                                errors={errors}
                                                style={{ width: 'auto', marginBottom: '1rem' }}
                                                labelOnLeft={true}
                                                spaceLabelContent={true}
                                                disabled={true}
                                            />
                                            <RHFInputBox
                                                label="Company Hourly Rate"
                                                name={`hourRate`}
                                                register={register}
                                                type="text"
                                                errors={errors}
                                                style={{ width: 'auto', marginBottom: '1rem' }}
                                                labelOnLeft={true}
                                                spaceLabelContent={true}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                }
                                {!selectedPersonnel && <div className="bold-centered">No Personnel Selected</div>}
                            </Panel>
                        </Panel>
                    </Page.Section>
                    {/* Top right panel */}
                    <Page.Section width={4}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="Position">
                                {selectedPersonnel && <div id="positionSideBar">
                                    <RHFCheckbox
                                        name="manager"
                                        label="Manager?"
                                        labelStyle={{ marginRight: '1.5rem', textAlign: 'center' }}
                                        register={register}
                                        bodyStyle={{ width: '50%' }}
                                        style={{ verticalAlign: 'middle' }}
                                        errors={errors}
                                        labelOnLeft={true}
                                    />
                                    <hr className="divider" />
                                    <div style={{ overflowY: 'scroll', height: '13rem' }}>
                                        {allPositions?.map((val) => {
                                            return (
                                                <div
                                                    key={parseInt(val.code, 10) - 1}
                                                    className="form-row"
                                                    style={{ height: '2rem', marginTop: '1rem' }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="position"
                                                        value={val.code.toString()}
                                                        ref={register}
                                                        checked={selectedEmployeePay?.position?.includes(Number(val.code))}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            const code = Number(val.code);

                                                            setSelectedEmployeePay(prev => ({
                                                                ...prev,
                                                                position: checked
                                                                    ? [...prev.position, code]
                                                                    : prev.position.filter(p => p !== code)
                                                            }));
                                                        }}
                                                    />
                                                    <label>{val.dscr}</label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <hr className="divider" />
                                    <br />
                                    <div className="dialog-col">
                                        <RHFSelect
                                            name={'paidBy'}
                                            options={[
                                                { value: 'D', label: 'Day' },
                                                { value: 'H', label: 'Hour' },
                                                { value: 'M', label: 'Month' },
                                            ]}
                                            label={'Paid By'}
                                            labelOnLeft={true}
                                            style={{ width: '80%' }}
                                            labelStyle={{ width: '20%' }}
                                            register={register}
                                            disabled={true}
                                        />
                                        <RHFSelect
                                            name={'billAs'}
                                            options={[
                                                { value: 'L', label: 'Landman' },
                                                { value: 'O', label: 'Office Backup' },
                                            ]}
                                            label={'Bill As'}
                                            labelOnLeft={true}
                                            style={{ width: '80%' }}
                                            labelStyle={{ width: '20%' }}
                                            register={register}
                                        />
                                        <RHFInputBox
                                            label="Def. Pay Rate"
                                            name={`defPayRate`}
                                            type="text"
                                            register={register}
                                            errors={errors}
                                            style={{ width: 'auto', marginBottom: '1rem' }}
                                            labelOnLeft={true}
                                            spaceLabelContent={true}
                                            disabled={true}
                                        />
                                        <RHFInputBox
                                            label="Bill Day Rate"
                                            name={`billDayRate`}
                                            type="text"
                                            register={register}
                                            errors={errors}
                                            style={{ width: 'auto', marginBottom: '1rem' }}
                                            labelOnLeft={true}
                                            spaceLabelContent={true}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="form-row" style={{ marginBottom: '0', justifyContent: 'center' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '35%', height: '3rem', marginLeft: '.5rem' }}
                                            text={'Update Billing'}
                                            onClick={() => showUpdateEmployeeBilling()}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '35%', height: '3rem', marginLeft: '1rem' }}
                                            text={'Billing History'}
                                            disabled={!isUserHR}
                                            onClick={() => showEmployeBillingHistory()}
                                        />
                                    </div>
                                </div>}
                                {!selectedPersonnel && <div className="bold-centered">No Personnel Selected</div>}
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
                {/* Bottom panel */}
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={10}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="Pay Exceptions">
                                {selectedPersonnel && <div className="form-row" style={{ marginBottom: '0' }}>
                                    <div
                                        className={isCollapsed ? 'small-panel' : 'small-panel-with-search'}
                                        style={{
                                            width: '78%',
                                            textAlign: 'center',
                                            overflowY: 'scroll'
                                        }}
                                    >
                                        {displayPayExceptions()}
                                    </div>
                                    <div style={{ top: '50%', width: '20%', marginLeft: '1rem', display: 'flex', flexDirection: 'column' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '100%', height: '3rem', marginTop: '.5rem' }}
                                            text={'Pay Exceptions History'}
                                            onClick={() => showProjectPayExceptionsHistory()}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '100%', height: '3rem', marginTop: '.5rem' }}
                                            text={'Billing Exceptions History'}
                                            onClick={() => showProjectBillExceptionsHistory()}
                                        />
                                    </div>
                                </div>}
                                {!selectedPersonnel && <div className="bold-centered">No Personnel Selected</div>}
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
            </form>
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
            <DefaultPayHistoryDialog
                dialogName={DialogList.DEFAULT_PAY_HISTORY}
                hideDialog={hideDialog}
                defaultPayHistory={defaultPayHistory}
            />
            <UpdatePayHistoryDialog
                dialogName={DialogList.UPDATE_PAY_HISTORY}
                showToast={showToast}
                hideDialog={hideDialog}
                currentRate={payRateProp ?? 0}
                currentUnit={payUnitProp ?? 'U'}
                selectedPersonnel={selectedPersonnel}
                updateScreenPay={updateScreenPay}
            />
            <DefaultBillHistoryDialog
                dialogName={DialogList.DEFAULT_BILL_HISTORY}
                hideDialog={hideDialog}
                defaultBillHistory={defaultBillHistory}
            />
            <UpdateBillHistoryDialog
                dialogName={DialogList.UPDATE_BILLING_HISTORY}
                hideDialog={hideDialog}
                currentRate={billRateProp ?? 0}
                currentUnit={billUnitProp ?? 'U'}
                selectedPersonnel={selectedPersonnel}
                updateScreenBilling={updateScreenBilling}
                showToast={showToast}
            />
            <PersonnelCommentDialog
                dialogName={DialogList.PERSONNEL_PERFORMANCE_COMMENTS}
                selectedPersonnel={selectedPersonnel}
                hideDialog={hideDialog}
            />
            <ProjectPayExceptionHistoryDialog
                dialogName={DialogList.PAY_EXCEPTIONS_AND_HISTORY}
                payExceptions={payExceptions}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
            />
            <ProjectBillExceptionAndHistoryDialog
                dialogName={DialogList.BILL_EXCEPTIONS_AND_HISTORY}
                billExceptions={billExceptions}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
            />
        </div>
    );
};

export default EmployeePayPage;



// WEBPACK FOOTER //
// ./src/components/personnel/pages/EmployeePayPage.tsx